/* eslint-disable react/display-name */
import React from 'react';
import { css } from 'styled-components';
import { withPrefix } from 'gatsby';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { renderRichText } from '@vhub/gatsby-source-contentful/rich-text';
import Heading from '@legacy-vfuk/core-heading';
import { GatsbyImage } from 'gatsby-plugin-image';
import CtaExternalLink from '../components/pageBuilder/Elements/CtaExternalLink';
import SeoCtaExternalLink from '../components/pageBuilder/Elements/SeoCtaExternalLink';
import CardCtaLink from '../components/pageBuilder/Elements/CardCtaLink';
import CtaInternalLink from '../components/pageBuilder/Elements/CtaInternalLink';
import SeoGatsbyLink from '../components/pageBuilder/Elements/SeoGatsbyLink';
import { Helmet } from 'react-helmet';
import ListGroup from '@legacy-vfuk/core-list-group';
import ListItem from '@legacy-vfuk/core-list-item';
import Divider from '@legacy-vfuk/core-divider';
import {
  blockquoteStyle,
  DividerWrapper,
  genericiframe,
  iframeContainer,
  videoiframe,
} from './styles/ContentFulOptions.styles';
import fireButtonAndLinkClicksEvent from './fireButtonAndLinkClicksEvent';
import QuoteCard from '../templates/QuoteCard';
import RedText from '../templates/RedText';

//let OneTrustHasBeenRendered = false;

/**Builder options for for rich text. We cater for;
 * Normal text (paragraph)
 * Heading1 to Heading6 (<Heading level={1}/> to <Heading level={6}/>)
 * Bold, Italic, Underline, Hyperlink, UL, OL and Blockquote.
 * From @contentful/rich-text-types/dist/types/marks, /blocks, /inlines are;
 *  MARKS:    BOLD, ITALIC, UNDERLINE, CODE.
 *  INLINES:  HYPERLINK, ENTRY_HYPERLINK, ASSET_HYPERLINK, EMBEDDED_ENTRY.
 *  BLOCKS:   DOCUMENT, PARAGRAPH, HEADING_1 to _6, OL_LIST, UL_LIST,
 *            LIST_ITEM, HR, QUOTE, EMBEDDED_ENTRY, EMBEDDED_ASSET.
 */

type Options = Required<Parameters<typeof renderRichText>[1]>;

const options: Options = {
  renderText: text => {
    if (text.includes('RedTextStart:') && text.includes(':RedTextEnd')) {
      const redTextMatch = text.match(/RedTextStart:(.*?):RedTextEnd/);
      const redText = redTextMatch ? redTextMatch[1] : '';
      const [before, after] = text.split(/RedTextStart:.*?:RedTextEnd/);

      return (
        <>
          {before && <>{before}</>}
          <RedText redText={redText} />
          {after && <>{after}</>}
        </>
      );
    }
    return text;
  },
  renderMark: {
    [MARKS.BOLD]: text => <span className="bold">{text}</span>,
    [MARKS.ITALIC]: text => <em>{text}</em>,
    [MARKS.UNDERLINE]: text => (
      <span className="underline">{text}</span>
    ) /*no underline in ws2*/,
    [MARKS.CODE]: text => (
      <pre>
        <code>{text}</code>
      </pre>
    ),
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (_node, children) => {
      return <Heading level={1}>{children}</Heading>;
    },
    [BLOCKS.HEADING_2]: (_node, children) => {
      return <Heading level={2}>{children}</Heading>;
    },
    [BLOCKS.HEADING_3]: (_node, children) => (
      <Heading level={3}>{children}</Heading>
    ),
    [BLOCKS.HEADING_4]: (_node, children) => (
      <Heading level={4}>{children}</Heading>
    ),
    [BLOCKS.HEADING_5]: (_node, children) => (
      <Heading level={5}>{children}</Heading>
    ),
    [BLOCKS.HEADING_6]: (_node, children) => (
      //@ts-expect-error HeadingKeys should only have 1-5 possible values, it will be rendered as h6 regardless
      <Heading level={6}>{children}</Heading>
    ),
    [BLOCKS.PARAGRAPH]: (_node, children) => {
      if (!children || !Array.isArray(children)) {
        return;
      }

      if (
        children.find(
          child => typeof child === 'string' && child.includes('scrollToId:')
        )
      ) {
        if (children.length === 1) {
          const splitChildren = children[0].split('scrollToId:');
          const indexOfFirstSpaceAfterSplit = splitChildren[1].indexOf(' ');
          let spanId = splitChildren[1].slice(0, indexOfFirstSpaceAfterSplit);
          if (indexOfFirstSpaceAfterSplit === -1) {
            spanId = splitChildren[1];
          }
          return (
            <div>
              {splitChildren[0]}
              <span id={spanId} />
              {splitChildren[1].slice(spanId.length)}
            </div>
          );
        } else {
          const spanId = children[1].split('scrollToId:')[1];
          return (
            <div>
              {children[0]}
              <span id={spanId} />
            </div>
          );
        }
      }

      if (
        children.find(
          child =>
            typeof child === 'string' &&
            child.includes('QuoteSectionStart:') &&
            child.includes(':QuoteSectionEnd')
        )
      ) {
        const cardText = children[0]
          .replace('QuoteSectionStart:', '')
          .replace(':QuoteSectionEnd', '');
        return <QuoteCard cardText={cardText} />;
      }

      if (
        children.find(
          child =>
            typeof child === 'string' && child.includes('OneTrustCookiesList')
        )
      ) {
        return <div id="ot-sdk-cookie-policy"></div>;
      }

      if (
        children.find(
          child =>
            typeof child === 'string' &&
            child.includes('OneTrustCookiesSettingsButton')
        )
      ) {
        return (
          <button id="ot-sdk-btn" className="ot-sdk-show-settings">
            Cookie Settings
          </button>
        );
      }

      return <p>{children}</p>;
    },
    [BLOCKS.OL_LIST]: (_node, children) => {
      if (!children || !Array.isArray(children)) {
        return;
      }

      return (
        <ListGroup listStyle="default" type="ordered">
          {children.map((child, index) => (
            <ListItem key={index} id={`ordered-${index}`}>
              {child.props.children[0]}
            </ListItem>
          ))}
        </ListGroup>
      );
    },
    [BLOCKS.UL_LIST]: (_node, children) => {
      if (!children || !Array.isArray(children)) {
        return;
      }
      return (
        <ListGroup listStyle="default" type="unordered">
          {children.map((child, index) => (
            <ListItem key={index} id={`unordered-${index}`}>
              {child.props.children[0]}
            </ListItem>
          ))}
        </ListGroup>
      );
    },
    [BLOCKS.HR]: () => (
      <DividerWrapper>
        <Divider appearance="secondary" />
      </DividerWrapper>
    ),
    [BLOCKS.QUOTE]: (_node, children) => (
      <blockquote css={blockquoteStyle}>{children}</blockquote>
    ),
    [BLOCKS.EMBEDDED_ASSET]: node => {
      const target = node.data?.target;
      if (!target) {
        return null;
      }

      const { gatsbyImageData, description, title, file } = target;

      switch (file.contentType) {
        case 'image/svg+xml':
          return (
            <img
              src={file.url}
              alt={description || title}
              width={file.details.image.width}
              height={file.details.image.height}
            />
          );

        default:
          if (!gatsbyImageData) {
            return null;
          }

          return (
            <GatsbyImage image={gatsbyImageData} alt={description || title} />
          );
      }
    },
    [INLINES.HYPERLINK]: node => {
      const { content } = node;

      const [{ value }] = content as unknown as [{ value: string }];

      switch (true) {
        case node.data.uri.includes('ctaExtLink'): {
          // CTA Link as an external link
          const { data } = node;
          let ctaLinkURL = data.uri.replace(/ctaExtLink:/i, '').trim();
          if (node.data.uri.includes('ctaSeoType')) {
            const seoString = ctaLinkURL.substring(ctaLinkURL.indexOf('?') + 1);
            ctaLinkURL = ctaLinkURL.substring(0, ctaLinkURL.indexOf('?'));
            return (
              <SeoCtaExternalLink
                label={value}
                href={ctaLinkURL}
                seoString={seoString}
              />
            );
          }
          return <CtaExternalLink label={value} href={ctaLinkURL} />;
        }
        case node.data.uri.includes('ctaIntLink'): {
          // CTA Link as an internal link
          const { data } = node;
          let ctaLinkURL = data.uri.replace(/ctaIntLink:/i, '').trim();
          if (data.uri.includes('ctaSeoType')) {
            const seoString = ctaLinkURL.substring(ctaLinkURL.indexOf('?') + 1);
            ctaLinkURL = ctaLinkURL.substring(0, ctaLinkURL.indexOf('?'));
            return (
              <SeoGatsbyLink
                label={value}
                href={ctaLinkURL}
                seoString={seoString}
              />
            );
          }
          return (
            <div
              onClick={() =>
                fireButtonAndLinkClicksEvent({
                  event_label: value,
                  event_action: 'button click',
                })
              }
            >
              <CtaInternalLink label={value} href={ctaLinkURL} />
            </div>
          );
        }
        case node.data.uri
          .toLowerCase()
          .includes('cardCtaLink'.toLowerCase()): {
          // CTA Link in a card as button
          let seoString = '';
          const { data } = node;
          let ctaLinkURL = data.uri.replace(/cardCtaLink:/i, '').trim();
          if (data.uri.includes('seoDataDetails:')) {
            seoString = ctaLinkURL
              .substring(ctaLinkURL.indexOf('?') + 1)
              .replace(/seoDataDetails:/i, '')
              .trim();
            ctaLinkURL = ctaLinkURL.substring(0, ctaLinkURL.indexOf('?'));
          }

          return (
            <CardCtaLink
              label={value}
              href={ctaLinkURL}
              seoString={seoString}
            />
          );
        }
        case node.data.uri.includes('youtube.com'): {
          // Youtube video.
          // NB - we don't specify height/width; the onPage is responsive to width.
          // For the Youtube iframe API, see:
          // https://developers.google.com/youtube/iframe_api_reference#Example_Video_Player_Constructors
          // Note - in onPlayerStateChange(), e.data; 1 = playing, 2 = paused, 0 = ended
          // Note - although this function will run per youtube video on the page, react-helmet
          //        will remove dupes.
          const youtubeId = `ytplayer_${node.data.uri.split('embed/')[1]}`;
          const apiScript = (
            <Helmet>
              <script src="https://www.youtube.com/iframe_api"></script>
              <script>{getYoutubeScript()}</script>
            </Helmet>
          );
          // NB - apiScript will appear in <head>
          return (
            <>
              {apiScript}
              <iframe
                css={videoiframe}
                id={youtubeId}
                className="yt-player"
                src={`${node.data.uri}?rel=0&enablejsapi=1`}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                type="text/html"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              />
            </>
          );
        }
        case node.data.uri.includes('iframe'): {
          // Generic iframe
          let iframeURL = node.data.uri.replace('iframe:', '');
          let height = 846;
          if (iframeURL.includes(' height=')) {
            const urlParams = iframeURL.split(' height=');
            iframeURL = urlParams[0];
            height = Number(urlParams[1]);
          }

          const heightStyle = css`
            height: ${height}px;
          `;
          return (
            <div css={[iframeContainer, heightStyle]}>
              <iframe
                css={genericiframe}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                type="text/html"
                src={iframeURL}
              />
            </div>
          );
        }
        default: {
          // Default vodafone hyperlink
          return (
            <a className="link link--body" href={withPrefix(node.data.uri)}>
              {value}
            </a>
          );
        }
      }
    },
  },
};

function getYoutubeScript() {
  /**
   * The e.data values are taken from the Youtube iframe API at
   * https://developers.google.com/youtube/iframe_api_reference
   * and are;
   * -1 - video 'unstarted' (we don't use this value)
   * 0  - video ended
   * 1  - video playing
   * 2  - video paused
   * 3  - video buffering (we don't use this value)
   * 5  - video cued (we don't use this value)
   */
  return `
  (function () {
    const playerStates = ["ended", "playing", "paused", "buffering", "videoCued"];
    let playerData = {};
    let hasInitialised = false;

    if (window.YT) {
      window.YT.ready(() => {
        setTimeout(() => {
          init();
        }, 500);
      });
    }


    window.onYouTubeIframeAPIReady = () => {
      init();
    }

    function init() {
      if (!hasInitialised) {
        const iframes = Array.from(document.getElementsByClassName('yt-player'));
        iframes.forEach(iframe => {
          const player = new YT.Player(iframe.id, {
            events: {
              'onStateChange': onPlayerStateChange
            }
          });
          playerData[iframe.id]  = {
            videoPoller: null,
            percentRun: -1,
            hasFinished: false,
            videoPlayed: false,
            lastPlayerState: "",
          }
        });
      }
      hasInitialised = true;
    }

    function onPlayerStateChange(e) {
      const id = e.target.getIframe().id;
      if (e.data === 1) {
        if (!playerData[id].videoPlayed) {
          update(e, 0);
          playerData[id].videoPlayed = true;
        }
        playerData[id].videoPoller = setInterval(function() {
          if (document.getElementById(id)) {
            update(e);
          } else {
            clearInterval(playerData[id].videoPoller);
          }
        }, 2000);
      } else if (e.data===2) {
        update(e)
        clearInterval(playerData[id].videoPoller);
      } else if (e.data===0) {
        if (!playerData[id].hasFinished) {
          update(e);
          playerData[id].hasFinished = true;
        }
        clearInterval(playerData[id].videoPoller);
      }
    }

    function update(event, percent=null) {
      const thePlayer = event.target;
      const id = event.target.getIframe().id;
      const currentTime = thePlayer.getCurrentTime();
      const duration = thePlayer.getDuration()
      const playerState = playerStates[thePlayer.getPlayerState()];
      const videoUrl = thePlayer.getVideoUrl();
      const newPercent = percent===null ? Math.floor(thePlayer.getVideoLoadedFraction()*10)*10 : percent;

      if (newPercent > playerData[id].percentRun || playerState!==playerData[id].lastPlayerState) {
        // Change the percent only if it is higher than the previous value.
        if (newPercent > playerData[id].percentRun) {
          playerData[id].percentRun = newPercent;
        }
        if (window.onVhubYtEvent) {
          const eventData = {
            type:'ytVideoEvent',
            percentRun:playerData[id].percentRun,
            currentTime,
            duration,
            playerState,
            videoUrl,
            source: {player: thePlayer, event},
          };
          window.onVhubYtEvent(eventData);
        }
      }
      playerData[id].lastPlayerState = playerState;
    }
  })();`;
}

export default options;
